

import { useFormik } from "formik";
import * as Yup from "yup";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import { Warehouse, RotationalProducts, Zone, Rack, Bin } from "types";
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
    ColumnDef,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";

type AllocationToBinsProps = {
    warehouses: Warehouse[];
    rotationalProduct: RotationalProducts;
    userId: string;
    token: string;
    productId: string;
};

type Combo = {
    warehouse_id: string;
    warehouse: string;
    zone_id: string;
    zone: string;
    rack_id: string;
    rack: string;
    bin_id: string;
    bin: string;
};

type ObjectId = string;

type WarehouseLocation = {
    warehouse_id?: ObjectId;
    warehouse?: string;
    zone_id?: ObjectId;
    zone?: string;
    rack_id?: ObjectId;
    rack?: string;
    bin_id?: ObjectId;
    bin?: string;
};

export type SerializedUnit = {
    serial_id: ObjectId;
    serial_code?: string;
    storage_location?: WarehouseLocation;
    condition?: string;
    status?: string;
    order_number?: string;
    created_at?: string;
    updated_at?: string;
};

type ApiRow = {
    type: "Bulk" | "Split" | string;
    warehouse: string;
    quantity_serial: string;  // number-as-string for bulk (e.g., "25"), or serial (e.g., "Pos1001")
    bin_location: string;     // e.g., "Bin A1-01"
    timestamp: string;        // ISO string
};
type Cards = {
    total_allocations: number;
    bulk_allocations: number;
    split_allocations: number;
};
// ===== Utils =====
const fmtTS = (iso?: string) => {
    if (!iso) return "---";
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const pill = (bg: string, fg: string) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 9999,
    fontWeight: 700,
    fontSize: 12,
    background: bg,
    color: fg,
    lineHeight: 1.4,
});

const dash = (v?: string | number | null) =>
    v === undefined || v === null || String(v).trim() === "" ? "---" : String(v);

// optional: compose a “Bin …” label
const getBinLabel = (loc?: WarehouseLocation) => {
    if (!loc) return "---";
    if (loc.bin) return `Bin ${loc.bin}`;
    const parts = [loc.zone, loc.rack, loc.bin].filter(Boolean).join("-");
    return parts ? `Bin ${parts}` : "---";
};


const AllocationToBins: React.FC<AllocationToBinsProps> = ({
    warehouses,
    rotationalProduct,
    userId,
    token,
    productId
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine the base route based on current location
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

    // State for parent signage firm (for SignageTeammember login)
    const [parentSignageId, setParentSignageId] = useState<string | null>(null);

    // signageId: parent signage firm when SignageTeammember, else userId
    const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : (userId || "");

    // Fetch parent hierarchy for SignageTeammember login
    useEffect(() => {
        const loadParentHierarchy = async () => {
            if (isSignageTeammember && token) {
                const hierarchyData = await fetchParentHierarchy(token);
                if (hierarchyData) {
                    setParentSignageId(hierarchyData.parentSignageId);
                }
            }
        };
        loadParentHierarchy();
    }, [isSignageTeammember, token]);

    // ===== State (unchanged logic) =====
    const [warehouseTotals, setWarehouseTotals] = useState({
        total: 0,
        serialized: 0,
        available: 0,
    });
    const [zones, setZones] = useState<Zone[]>([]);
    const [racks, setRacks] = useState<Rack[]>([]);
    const [bins, setBins] = useState<Bin[]>([]);
    const [loading, setLoading] = useState(true);

    const [availableSerializedUnits, setAvailableSerializedUnits] = useState<any[]>([]);

    // ===== Data fetchers (unchanged behavior) =====
    const fetchWarehouseSerializationData = async (warehouseId: string) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational-products/${rotationalProduct._id}/warehouses/${warehouseId}/serial-count`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await res.json();
            // console.log("Serialization data:", data);
            if (!res.ok) {
                showError(data.message || "Failed to fetch warehouse serialization data");
                return;
            }

            // Map API response to warehouse totals
            setWarehouseTotals({
                total: data.available || 0,  // Total Quantity
                serialized: data.serialized_unit_count || 0,  // Serialized Quantity
                available: data.unallocated_serial_count || 0,  // Available Quantity (for allocation)
            });
        } catch (err) {
            console.error("Serialization fetch error", err);
            showError("Error getting warehouse serialization data");
        }
    };

    const fetchSerializedUnits = async (rotationalId: string, warehouseId: string) => {
        if (!signageId || !token) return;

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/signage/${signageId}/warehouses/rotational-pool/${rotationalId}/warehouse/${warehouseId}/available-serialized-units`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || "Failed to fetch serialized pool");
            console.log("Serialized Units:", data);
            setAvailableSerializedUnits(data);
        } catch (err: any) {
            console.error("Fetch Serialized Units Error:", err.message);
            showError("Unable to fetch serialized units");
        }
    };

    const fetchAllZones = async (): Promise<Zone[]> => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/zones`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.message || "Failed to fetch zones");
            return data;
        } catch (err) {
            console.error("Zones fetch error", err);
            showError("Error getting zones");
            return [];
        }
    };
    const fetchAllRacks = async (): Promise<Rack[]> => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/racks`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.message || "Failed to fetch racks");
            return data;
        } catch (err) {
            console.error("Racks fetch error", err);
            showError("Error getting racks");
            return [];
        }
    };
    const fetchAllBins = async (): Promise<Bin[]> => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/bins`, {
                method: "GET",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.message || "Failed to fetch bins");
            return data;
        } catch (err) {
            console.error("Bins fetch error", err);
            showError("Error getting bins");
            return [];
        }
    };

    useEffect(() => {
        (async () => {
            const [z, r, b] = await Promise.all([fetchAllZones(), fetchAllRacks(), fetchAllBins()]);
            setZones(z);
            setRacks(r);
            setBins(b);
        })();
    }, []);

    // Build combinations to render "Bin Location" paths
    const combinations: Combo[] = useMemo(() => {
        const whName: Record<string, string> = {};
        warehouses?.forEach((w: any) => (whName[w._id] = w.label));

        const out: Combo[] = [];
        racks?.forEach((rk: any) => {
            const z = zones.find((zz: any) => zz._id === rk.zone_id);
            bins.forEach((bn: any) => {
                if (bn.rack_id === rk._id) {
                    out.push({
                        warehouse_id: z?.warehouse_id || rk.warehouse_id || "",
                        warehouse: whName[z?.warehouse_id || rk.warehouse_id || ""] || "",
                        zone_id: z?._id || "",
                        zone: z?.label || "",
                        rack_id: rk?._id || "",
                        rack: rk?.label || rk?.rack || rk?.name || "",
                        bin_id: bn?._id || "",
                        bin: bn?.label || bn?.bin || bn?.name || "",
                    });
                }
            });
        });
        return out;
    }, [warehouses, zones, racks, bins]);

    // ===== Formik (fields preserved) =====
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            warehouseId: "",
            quantity: "",
            mode: "bulk",
            bulkBin: "",
            splits: [{ serial_id: "", serial_number: "", binId: "" }],
        },
        validationSchema: Yup.object({}),
        onSubmit: async (values) => {
            console.log("Form Values:", values);
            try {
                // Bulk Allocation
                if (values.mode === "bulk") {
                    // Find the selected bin's full combination
                    console.log("bulk :")
                    const combo = combinations.find(c => c.bin_id === values.bulkBin);
                    if (!combo) {
                        showError("Please select a valid bin.");
                        return;
                    }

                    const payload = {
                        mode: "bulk",
                        warehouse_id: values.warehouseId,
                        count: Number(values.quantity),
                        allocations: [
                            {
                                location: {
                                    warehouse_id: combo.warehouse_id,
                                    warehouse_name: combo.warehouse,
                                    zone_id: combo.zone_id,
                                    zone_name: combo.zone,
                                    rack_id: combo.rack_id,
                                    rack_name: combo.rack,
                                    bin_id: combo.bin_id,
                                    bin_name: combo.bin,
                                }
                            }]
                    };

                    const res = await fetch(
                        `${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational-products/${rotationalProduct._id}/allocate-serialized`,
                        {
                            method: "PUT",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(payload),
                        }
                    );
                    const data = await res.json();
                    
                    // Check for error in response
                    if (!res.ok || data?.error) {
                        const errorMsg = data?.error || data?.message || "Bulk allocation failed";
                        showError(errorMsg);
                        return;
                    }
                    
                    // Show success message from backend or default
                    const successMsg = data?.message || "Bulk allocation successful!";
                    showSuccess(successMsg);
                    
                    navigate(`${baseRoute}/rotationalProducts`, {
                        state: { successMessage: successMsg },
                    });
                }

                // Split Allocation
                if (values.mode === "split") {
                    console.log("split :")
                    // Group splits by binId for API structure
                    const allocationsMap: { [binId: string]: { serialized_unit_ids: string[], combo: Combo } } = {};
                    values.splits.forEach(split => {
                        if (!split.binId || !split.serial_id) return;
                        const combo = combinations.find(c => c.bin_id === split.binId);
                        if (!combo) return;
                        if (!allocationsMap[split.binId]) {
                            allocationsMap[split.binId] = { serialized_unit_ids: [], combo };
                        }
                        allocationsMap[split.binId].serialized_unit_ids.push(split.serial_id);
                    });

                    // Convert map to allocations array
                    const allocations = Object.values(allocationsMap)?.map(({ serialized_unit_ids, combo }) => ({
                        serialized_unit_ids,
                        location: {
                            warehouse_id: combo.warehouse_id,
                            warehouse_name: combo.warehouse,
                            zone_id: combo.zone_id,
                            zone_name: combo.zone,
                            rack_id: combo.rack_id,
                            rack_name: combo.rack,
                            bin_id: combo.bin_id,
                            bin_name: combo.bin,
                        }
                    }));

                    if (allocations.length === 0) {
                        showError("Please allocate at least one serial to a bin.");
                        return;
                    }

                    const payload = {
                        mode: "split",
                        warehouse_id: values.warehouseId,
                        allocations,
                    };

                    const res = await fetch(
                        `${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational-products/${rotationalProduct._id}/allocate-serialized`,
                        {
                            method: "PUT",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(payload),
                        }
                    );
                    const data = await res.json();
                    
                    // Check for error in response
                    if (!res.ok || data?.error) {
                        const errorMsg = data?.error || data?.message || "Split allocation failed";
                        showError(errorMsg);
                        return;
                    }
                    
                    // Show success message from backend or default
                    const successMsg = data?.message || "Split allocation successful!";
                    showSuccess(successMsg);
                    
                    navigate(`${baseRoute}/rotationalProducts`, {
                        state: { successMessage: successMsg },
                    });
                }

            } catch (err: any) {
                showError(err.message || "Error in allocation");
            }
        },
    });

    const filteredCombinations = useMemo(
        () => combinations.filter((c) => !formik.values.warehouseId || c.warehouse_id === formik.values.warehouseId),
        [combinations, formik.values.warehouseId]
    );

    useEffect(() => {
        if (rotationalProduct && formik.values.warehouseId) {
            fetchSerializedUnits(rotationalProduct._id, formik.values.warehouseId);
        }
    }, [rotationalProduct, formik.values.warehouseId, signageId]);

    // helpers
    const addSplit = () =>
        formik.setFieldValue("splits", [...formik.values.splits, { serial_id: "", serial_number: "", binId: "" }]);
    const removeSplit = (index: number) =>
        formik.setFieldValue(
            "splits",
            formik.values.splits.filter((_, i) => i !== index)
        );

    // enable bulk CTA only when quantity > 0 and a bin is chosen AND available > 0
    const isBulkReady = useMemo(
        () => Number(formik.values.quantity) > 0 && Boolean(formik.values.bulkBin) && warehouseTotals.available > 0,
        [formik.values.quantity, formik.values.bulkBin, warehouseTotals.available]
    );

    // enable split submit only when at least one row has BOTH serial and bin AND available > 0
    const isSplitReady = useMemo(
        () => (formik.values.splits || []).some((s: any) => s.serial_id && s.binId) && warehouseTotals.available > 0,
        [formik.values.splits, warehouseTotals.available]
    );


    /* ---------- datatable state ---------- */
    const [rows, setRows] = useState<ApiRow[]>([]);
    const [cards, setCards] = useState<Cards>({
        total_allocations: 0,
        bulk_allocations: 0,
        split_allocations: 0,
    });

    // fetch table + cards whenever warehouse changes
    useEffect(() => {
        const wid = String(formik.values.warehouseId || "");
        setRows([]);
        setCards({ total_allocations: 0, bulk_allocations: 0, split_allocations: 0 });
        if (!wid) return;

        setLoading(true);
        (async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational/${productId}/allocations/datatable`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            page: 1,
                            limit: 50,
                            search: "",
                            warehouse_id: wid,
                            sort: { field: "timestamp", order: "desc" },
                        }),
                    }
                );
                const result: any = await response.json();
                if (!response.ok || result?.error) {
                    throw new Error(result?.error || result?.message || "Failed to load allocations");
                }
                const data: ApiRow[] = Array.isArray(result?.data) ? result.data : [];
                setRows(data);
                setCards({
                    total_allocations: Number(result?.cards?.total_allocations || data.length),
                    bulk_allocations: Number(result?.cards?.bulk_allocations || 0),
                    split_allocations: Number(result?.cards?.split_allocations || 0),
                });
            } catch (err) {
                console.error(err);
                showError("Error loading allocations");
            } finally {
                setLoading(false);
            }
        })();
    }, [formik.values.warehouseId, productId, token, signageId]);

    /* ---------- table columns (match API) ---------- */
    const columnHelper = createColumnHelper<ApiRow>();
    const columns = useMemo<ColumnDef<ApiRow, any>[]>(() => {
        return [
            columnHelper.accessor("type", {
                header: () => <div className="th-text">Type</div>,
                cell: (info) => {
                    const value = info.getValue();
                    const isBulk = String(value).toLowerCase() === "bulk";
                    const style = pill(isBulk ? "#E0F2FE" : "#F3E8FF", isBulk ? "#075985" : "#7B1FA2");
                    return <span style={style}>{value}</span>;
                },
            }),
            columnHelper.accessor("warehouse", {
                header: () => <div className="th-text">Warehouse</div>,
                cell: (info) => <div className="text-sm">{info.getValue() || "-"}</div>,
            }),
            columnHelper.accessor("quantity_serial", {
                header: () => <div className="th-text">Quantity/Serial</div>,
                cell: (info) => (
                    <div className="text-sm" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                        {info.getValue() || "-"}
                    </div>
                ),
            }),
            columnHelper.accessor("bin_location", {
                header: () => <div className="th-text">Bin Location</div>,
                cell: (info) => <div className="text-sm">{info.getValue() || "-"}</div>,
            }),
            columnHelper.accessor("timestamp", {
                header: () => <div className="th-text">Timestamp</div>,
                cell: (info) => <div className="text-sm">{fmtTS(info.getValue())}</div>,
            }),
        ];
    }, []);

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <form onSubmit={formik.handleSubmit} className="p-0 p-lg-4">
            <h4 className="mb-4 fw-bold">Allocation - Assets Product</h4>
            <div className="row mb-3">
                <div className="col-lg-4">
                    <label className="form-label fs-13">Select Warehouse</label>
                    <select
                        className="form-select"
                        name="warehouseId"
                        value={formik.values.warehouseId}
                        onChange={(e) => {
                            formik.handleChange(e);
                            // Only fetch data if a valid warehouse is selected (not empty)
                            if (e.target.value) {
                                fetchWarehouseSerializationData(e.target.value);
                            } else {
                                // Reset warehouse totals when no warehouse is selected
                                setWarehouseTotals({
                                    total: 0,
                                    serialized: 0,
                                    available: 0,
                                });
                            }
                            setRows([]);
                        }}
                    >
                        <option value="">Select Warehouse</option>
                        {warehouses?.map((w: any) => (
                            <option key={w._id} value={w._id}>
                                {w.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="row g-3 mb-4">
                <div className="col-lg-4">
                    <div className="border-2 br-8 card shadow-lg">
                        <div className="card-body text-center">
                            <div className="fs-13 fw-medium mb-1 small" style={{ color: "#4a5565" }}>Total Quantity</div>
                            <div className="fs-21 fw-bold" style={{ color: "#101828" }}>{warehouseTotals?.total}</div>
                            <div className="text-muted small fs-12">
                                in {warehouses?.find((w: any) => w._id === formik.values.warehouseId)?.label || "—"}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="border-2 br-8 card shadow-lg">
                        <div className="card-body text-center">
                            <div className="fs-13 fw-medium mb-1 small" style={{ color: "#4a5565" }}>Serialized Quantity</div>
                            <div className="fs-21 fw-bold" style={{ color: "#155dfc" }}>{warehouseTotals?.serialized}</div>
                            <div className="text-muted small fs-12">
                                in {warehouses?.find((w: any) => w._id === formik.values.warehouseId)?.label || "—"}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="border-2 br-8 card shadow-lg">
                        <div className="card-body text-center">
                            <div className="fs-13 fw-medium mb-1 small" style={{ color: "#4a5565" }}>Available For Bin Allocations</div>
                            <div className="fs-21 fw-bold" style={{ color: "#00a63e" }}>{warehouseTotals?.available}</div>
                            <div className="text-muted small fs-12">
                                in {warehouses?.find((w: any) => w._id === formik.values.warehouseId)?.label || "—"}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-3">
                <div className="alloc-toggle d-inline-flex rounded-pill p-1">
                    <button
                        type="button"
                        aria-pressed={formik.values.mode === "bulk"}
                        className={`btn rounded-pill btn-alloc btn-alloc-bulk ${formik.values.mode === "bulk" ? "active" : ""}`}
                        onClick={() => formik.setFieldValue("mode", "bulk")}
                    >
                        Bulk Allocation
                    </button>
                    <button
                        type="button"
                        aria-pressed={formik.values.mode === "split"}
                        className={`btn rounded-pill btn-alloc btn-alloc-split ${formik.values.mode === "split" ? "active" : ""}`}
                        onClick={() => formik.setFieldValue("mode", "split")}
                    >
                        Split Allocation
                    </button>
                </div>
            </div>

            {formik.values.mode === "bulk" && (
                <div className="row g-3 align-items-end mt-3">
                    <div className="col-lg-4">
                        <label className="form-label fs-13">Quantity</label>
                        <input
                            type="number"
                            name="quantity"
                            className="form-control"
                            placeholder="Enter quantity"
                            value={formik.values.quantity as any}
                            onChange={formik.handleChange}
                        />
                    </div>
                    <div className="align-items-center col-lg-4 d-lg-flex gap-5 offset-lg-1">
                        <div className="col-lg-12">
                            <label className="form-label fs-13">Bin Location</label>
                            <select
                                className="form-select"
                                name="bulkBin"
                                value={formik.values.bulkBin as any}
                                onChange={formik.handleChange}
                            >
                                <option value="">Select Bin Location</option>
                                {filteredCombinations?.map((combo) => (
                                    <option key={combo.bin_id} value={combo.bin_id}>
                                        {combo.warehouse} &gt; {combo.zone} &gt; {combo.rack} &gt; {combo.bin}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-lg-5 text-lg-end">
                            <button type="submit" className="btn btn-primary table-addbutton mt-4" disabled={!isBulkReady}>
                                Allocate Bulk
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {formik.values.mode === "split" && (
                <div className="mt-4">
                    <div className="fs-18 fw-bold mb-2">Split Allocations</div>

                    {formik.values.splits?.map((item: any, index: number) => (
                        <div key={index} className="align-items-center card flex-row g-3 mb-2 row mt-2 border-1 br-8 p-3">
                            <div className="col-lg-4 mt-0 mb-3 mb-lg-0">
                                <label className="form-label fs-13">Select Serial Number</label>
                                <select
                                    className="form-select"
                                    name={`splits[${index}].serial_id`}
                                    value={item.serial_id}
                                    onChange={(e) => {
                                        const serial_id = e.target.value;
                                        const serial_number =
                                            availableSerializedUnits.find((u: any) => u.serial_id === serial_id)?.serial_number || "";
                                        const newSplits = [...(formik.values.splits as any[])];
                                        newSplits[index] = { ...newSplits[index], serial_id, serial_number };
                                        formik.setFieldValue("splits", newSplits);
                                    }}
                                >
                                    <option value="">Select Serial Number</option>
                                    {availableSerializedUnits?.map((unit: any) => (
                                        <option key={unit.serial_id} value={unit.serial_id}>
                                            {unit.serial_number}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-lg-4 mt-0 mb-3 mb-lg-0">
                                <label className="form-label fs-13">Select Bin</label>
                                <select
                                    name={`splits[${index}].binId`}
                                    className="form-select"
                                    value={item.binId}
                                    onChange={formik.handleChange}
                                >
                                    <option value="">Select Bin</option>
                                    {filteredCombinations?.map((combo) => (
                                        <option key={combo.bin_id} value={combo.bin_id}>
                                            {combo.warehouse} &gt; {combo.zone} &gt; {combo.rack} &gt; {combo.bin}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-lg-2 mt-0">
                                <label className="form-label d-none d-lg-block fs-13">&nbsp;</label>
                                <div>
                                    <button
                                        type="button"
                                        className={`btn text-danger fs-1 ${index === 0 ? "disabled" : ""} border-0 p-0`}
                                        onClick={() => removeSplit(index)}
                                        aria-label="Remove row"
                                        disabled={index === 0}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}

                    <button type="button" className="btn btn-add-split mt-2 br-8" onClick={addSplit}>
                        + Add Bin Allocation
                    </button>
                </div>
            )}
            <div className="row mt-4">
                <div className="col-12 d-flex justify-content-between mb-3">
                    {
                        formik.values.mode === "split" && (
                            <button type="submit" className="btn btn-primary table-addbutton" disabled={!isSplitReady}>
                                Submit Allocation
                            </button>
                        )
                    }
                </div>
            </div>

            <div className="card   shadow-border-datatables">
                <div className="rdt-desktop">
                    <div
                     
                    >
                        <table className="table  align-middle mb-0 table-1">
                            <DesktopTableParts table={table} />
                        </table>
                    </div>
                </div>

                {/* MOBILE (≤ 1021px) */}
                <div className="rdt-mobile">
                    <MobileCardsOnly
                        table={table}
                        idColId="order_link"
                        titleColId="product_name"
                        actionColId="actions"
                    />
                </div>

            </div>
            {/* {table.getRowModel().rows.length === 0 ? (
                    <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
                      No records found
                    </div>
                  ) : (
                    <div className="mt-4 mb-4">
                      <TablePaginationControls table={table} />
                    </div>
                  )} */}

            <div className="mt-2">
                <div className="row g-3">
                    <div className="col-md-4">
                        <div className="card border rounded-4 shadow-sm">
                            <div className="card-body text-center">
                                <div className=" mb-1">Total Allocations</div>
                                <div className="fs-3 fw-bold" style={{ color: "#101828" }}>{cards.total_allocations}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border rounded-4 shadow-sm">
                            <div className="card-body text-center">
                                <div className=" mb-1">Bulk Allocations</div>
                                <div className="fs-3 fw-bold" style={{ color: "#155dfc" }}>{cards.bulk_allocations}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card border rounded-4 shadow-sm">
                            <div className="card-body text-center">
                                <div className=" mb-1">Split Allocations</div>
                                <div className="fs-3 fw-bold" style={{ color: "#9810fa" }}>{cards.split_allocations}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* styles */}
            <style>{`
        .alloc-toggle { background:#f3f4f6; border:1px solid #e5e7eb; }
        .btn-alloc { margin:2px; min-width:180px; font-weight:600; background:#f3f4f6; color:#4b5563; border:1px solid #e5e7eb; }
        .btn-alloc:hover { background:#fff; color:#000; transform:scale(1.03); }
        .btn-alloc-bulk.active { background:#eff6ff; color:#1447e6; border-color:#bedbff; box-shadow:0 4px 12px rgba(20,71,230,.12) }
        .btn-alloc-split.active { background:#faf5ff; color:#8200db; border-color:#e9d4ff; box-shadow:0 4px 12px rgba(130,0,219,.12) }
        .btn-add-split{ background:#f3f4f6; color:#4b5563; border:1px solid #d1d5db; font-weight:600; }
        .btn-add-split:hover,.btn-add-split:focus{ background:#fff; color:#000; border-color:#000; }
      `}</style>
        </form>
    );
};

export default AllocationToBins;






























// import React, { useEffect, useMemo, useState } from "react";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";
// import { Warehouse, RotationalProducts, Zone, Rack, Bin } from "types";

// type AllocationToBinsProps = {
//     warehouses: Warehouse[];
//     rotationalProduct: RotationalProducts;
//     userId: string;
//     token: string;
// };

// type Combo = {
//     warehouse_id: string;
//     warehouse: string;
//     zone_id: string;
//     zone: string;
//     rack_id: string;
//     rack: string;
//     bin_id: string;
//     bin: string;
// };

// const AllocationToBins: React.FC<AllocationToBinsProps> = ({
//     warehouses,
//     rotationalProduct,
//     userId,
//     token,
// }) => {
//     const navigate = useNavigate();

//     // ===== State (unchanged logic) =====
//     const [warehouseTotals, setWarehouseTotals] = useState({
//         total: 0,
//         serialized: 0,
//         available: 0,
//     });
//     const [zones, setZones] = useState<Zone[]>([]);
//     const [racks, setRacks] = useState<Rack[]>([]);
//     const [bins, setBins] = useState<Bin[]>([]);
//     const [availableSerializedUnits, setAvailableSerializedUnits] = useState<any[]>([]);

//     // ===== Data fetchers (unchanged behavior) =====
//     const fetchWarehouseSerializationData = async (warehouseId: string) => {
//         try {
//             const res = await fetch(
//                 `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${rotationalProduct._id}/warehouses/${warehouseId}/serial-count`,
//                 {
//                     method: "GET",
//                     headers: {
//                         Authorization: `Bearer ${token}`,
//                         "Content-Type": "application/json",
//                     },
//                 }
//             );

//             const data = await res.json();
//             // console.log("Serialization data:", data);
//             if (!res.ok) {
//                 toast.error(data.message || "Failed to fetch warehouse serialization data");
//                 return;
//             }

//             setWarehouseTotals({
//                 total: data.available,
//                 serialized: data.serialized_unit_count,
//                 available: data.available - data.serialized_unit_count,
//             });
//         } catch (err) {
//             console.error("Serialization fetch error", err);
//             toast.error("Error getting warehouse serialization data");
//         }
//     };

//     const fetchSerializedUnits = async (rotationalId: string, warehouseId: string) => {
//         if (!userId || !token) return;

//         try {
//             const response = await fetch(
//                 `${process.env.REACT_APP_API_URL}/signage/${userId}/warehouses/rotational-pool/${rotationalId}/warehouse/${warehouseId}/available-serialized-units`,
//                 {
//                     method: "GET",
//                     headers: {
//                         Authorization: `Bearer ${token}`,
//                         "Content-Type": "application/json",
//                     },
//                 }
//             );

//             const data = await response.json();
//             if (!response.ok) throw new Error(data.message || "Failed to fetch serialized pool");
//             console.log("Serialized Units:", data);
//             setAvailableSerializedUnits(data);
//         } catch (err: any) {
//             console.error("Fetch Serialized Units Error:", err.message);
//             toast.error("Unable to fetch serialized units");
//         }
//     };

//     const fetchAllZones = async (): Promise<Zone[]> => {
//         try {
//             const res = await fetch(`${process.env.REACT_APP_API_URL}/zones`, {
//                 method: "GET",
//                 headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//             });
//             const data = await res.json();
//             if (!res.ok) throw new Error(data?.message || "Failed to fetch zones");
//             return data;
//         } catch (err) {
//             console.error("Zones fetch error", err);
//             toast.error("Error getting zones");
//             return [];
//         }
//     };
//     const fetchAllRacks = async (): Promise<Rack[]> => {
//         try {
//             const res = await fetch(`${process.env.REACT_APP_API_URL}/racks`, {
//                 method: "GET",
//                 headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//             });
//             const data = await res.json();
//             if (!res.ok) throw new Error(data?.message || "Failed to fetch racks");
//             return data;
//         } catch (err) {
//             console.error("Racks fetch error", err);
//             toast.error("Error getting racks");
//             return [];
//         }
//     };
//     const fetchAllBins = async (): Promise<Bin[]> => {
//         try {
//             const res = await fetch(`${process.env.REACT_APP_API_URL}/bins`, {
//                 method: "GET",
//                 headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//             });
//             const data = await res.json();
//             if (!res.ok) throw new Error(data?.message || "Failed to fetch bins");
//             return data;
//         } catch (err) {
//             console.error("Bins fetch error", err);
//             toast.error("Error getting bins");
//             return [];
//         }
//     };

//     useEffect(() => {
//         (async () => {
//             const [z, r, b] = await Promise.all([fetchAllZones(), fetchAllRacks(), fetchAllBins()]);
//             setZones(z);
//             setRacks(r);
//             setBins(b);
//         })();
//     }, []);

//     // Build combinations to render "Bin Location" paths
//     const combinations: Combo[] = useMemo(() => {
//         const whName: Record<string, string> = {};
//         warehouses?.forEach((w: any) => (whName[w._id] = w.label));

//         const out: Combo[] = [];
//         racks.forEach((rk: any) => {
//             const z = zones.find((zz: any) => zz._id === rk.zone_id);
//             bins.forEach((bn: any) => {
//                 if (bn.rack_id === rk._id) {
//                     out.push({
//                         warehouse_id: z?.warehouse_id || rk.warehouse_id || "",
//                         warehouse: whName[z?.warehouse_id || rk.warehouse_id || ""] || "",
//                         zone_id: z?._id || "",
//                         zone: z?.label || "",
//                         rack_id: rk?._id || "",
//                         rack: rk?.label || rk?.rack || rk?.name || "",
//                         bin_id: bn?._id || "",
//                         bin: bn?.label || bn?.bin || bn?.name || "",
//                     });
//                 }
//             });
//         });
//         return out;
//     }, [warehouses, zones, racks, bins]);

//     // ===== Formik (fields preserved) =====
//     const formik = useFormik({
//         enableReinitialize: true,
//         initialValues: {
//             warehouseId: "",
//             quantity: "",
//             mode: "bulk",
//             bulkBin: "",
//             splits: [{ serial_id: "", serial_number: "", binId: "" }],
//         },
//         validationSchema: Yup.object({}),
//         onSubmit: async (values) => {
//             console.log("Form Values:", values);
//             try {
//                 // Bulk Allocation
//                 if (values.mode === "bulk") {
//                     // Find the selected bin's full combination
//                     console.log("bulk :")
//                     const combo = combinations.find(c => c.bin_id === values.bulkBin);
//                     if (!combo) {
//                         toast.error("Please select a valid bin.");
//                         return;
//                     }

//                     const payload = {
//                         mode: "bulk",
//                         warehouse_id: values.warehouseId,
//                         count: Number(values.quantity),
//                         allocations: [
//                             {
//                                 location: {
//                                     warehouse_id: combo.warehouse_id,
//                                     warehouse: combo.warehouse,
//                                     zone_id: combo.zone_id,
//                                     zone: combo.zone,
//                                     rack_id: combo.rack_id,
//                                     rack: combo.rack,
//                                     bin_id: combo.bin_id,
//                                     bin: combo.bin,
//                                 }
//                             }]
//                     };

//                     const res = await fetch(
//                         `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${rotationalProduct._id}/allocate-serialized`,
//                         {
//                             method: "PUT",
//                             headers: {
//                                 Authorization: `Bearer ${token}`,
//                                 "Content-Type": "application/json",
//                             },
//                             body: JSON.stringify(payload),
//                         }
//                     );
//                     const data = await res.json();
//                     if (!res.ok) throw new Error(data.message || "Allocation failed");
//                     // toast.success("Bulk allocation successful!");
//                     navigate("/signage/rotationalProducts", {
//                         state: { successMessage: "Bulk allocation successful!" },
//                     });
//                     // Optionally: reload or update state
//                 }

//                 // Split Allocation
//                 if (values.mode === "split") {
//                     console.log("split :")
//                     // Group splits by binId for API structure
//                     const allocationsMap: { [binId: string]: { serialized_unit_ids: string[], combo: Combo } } = {};
//                     values.splits.forEach(split => {
//                         if (!split.binId || !split.serial_id) return;
//                         const combo = combinations.find(c => c.bin_id === split.binId);
//                         if (!combo) return;
//                         if (!allocationsMap[split.binId]) {
//                             allocationsMap[split.binId] = { serialized_unit_ids: [], combo };
//                         }
//                         allocationsMap[split.binId].serialized_unit_ids.push(split.serial_id);
//                     });

//                     // Convert map to allocations array
//                     const allocations = Object.values(allocationsMap)?.map(({ serialized_unit_ids, combo }) => ({
//                         serialized_unit_ids,
//                         location: {
//                             warehouse_id: combo.warehouse_id,
//                             warehouse: combo.warehouse,
//                             zone_id: combo.zone_id,
//                             zone: combo.zone,
//                             rack_id: combo.rack_id,
//                             rack: combo.rack,
//                             bin_id: combo.bin_id,
//                             bin: combo.bin,
//                         }
//                     }));

//                     if (allocations.length === 0) {
//                         toast.error("Please allocate at least one serial to a bin.");
//                         return;
//                     }

//                     const payload = {
//                         mode: "split",
//                         warehouse_id: values.warehouseId,
//                         allocations,
//                     };

//                     const res = await fetch(
//                         `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${rotationalProduct._id}/allocate-serialized`,
//                         {
//                             method: "PUT",
//                             headers: {
//                                 Authorization: `Bearer ${token}`,
//                                 "Content-Type": "application/json",
//                             },
//                             body: JSON.stringify(payload),
//                         }
//                     );
//                     const data = await res.json();
//                     if (!res.ok) throw new Error(data.message || "Split allocation failed");
//                     // toast.success("Split allocation successful!");
//                     navigate("/signage/rotationalProducts", {
//                         state: { successMessage: "Split allocation successful!" },
//                     });
//                     // Optionally: reload or update state
//                 }

//             } catch (err: any) {
//                 toast.error(err.message || "Error in allocation");
//             }
//         },
//     });

//     const filteredCombinations = useMemo(
//         () => combinations.filter((c) => !formik.values.warehouseId || c.warehouse_id === formik.values.warehouseId),
//         [combinations, formik.values.warehouseId]
//     );

//     useEffect(() => {
//         if (rotationalProduct && formik.values.warehouseId) {
//             fetchSerializedUnits(rotationalProduct._id, formik.values.warehouseId);
//         }
//     }, [rotationalProduct, formik.values.warehouseId]);

//     // helpers
//     const addSplit = () =>
//         formik.setFieldValue("splits", [...formik.values.splits, { serial_id: "", serial_number: "", binId: "" }]);
//     const removeSplit = (index: number) =>
//         formik.setFieldValue(
//             "splits",
//             formik.values.splits.filter((_, i) => i !== index)
//         );

//     // enable bulk CTA only when quantity > 0 and a bin is chosen
//     const isBulkReady = useMemo(
//         () => Number(formik.values.quantity) > 0 && Boolean(formik.values.bulkBin),
//         [formik.values.quantity, formik.values.bulkBin]
//     );

//     // enable split submit only when at least one row has BOTH serial and bin
//     const isSplitReady = useMemo(
//         () => (formik.values.splits || []).some((s: any) => s.serial_id && s.binId),
//         [formik.values.splits]
//     );


//     return (
//         <form onSubmit={formik.handleSubmit} className="p-0 p-lg-4">
//             <h4 className="mb-4 fw-bold">Allocation - Assets Product</h4>

//             {/* Warehouse + Available card */}
//             <div className="row mb-3">
//                 <div className="col-lg-4">
//                     <label className="form-label fs-13">Select Warehouse</label>
//                     <select
//                         className="form-select"
//                         name="warehouseId"
//                         value={formik.values.warehouseId}
//                         onChange={(e) => {
//                             formik.handleChange(e);
//                             fetchWarehouseSerializationData(e.target.value);
//                         }}
//                     >
//                         <option value="">Select Warehouse</option>
//                         {warehouses?.map((w: any) => (
//                             <option key={w._id} value={w._id}>
//                                 {w.label}
//                             </option>
//                         ))}
//                     </select>
//                 </div>
//             </div>

//             <div className="col-lg-4">
//                 <div className="border-2 br-8 card shadow-lg">
//                     <div className="card-body">
//                         <div className="fs-13 fw-medium mb-1 small" style={{ color: "#4a5565" }}>Available Quantity</div>
//                         <div className="fs-21 fw-bold" style={{ color: "#00a63e" }}>{warehouseTotals.serialized}</div>
//                         <div className="text-muted small fs-12">
//                             in {warehouses.find((w: any) => w._id === formik.values.warehouseId)?.label || "—"}
//                         </div>
//                     </div>
//                 </div>
//             </div>


//             {/* Mode switch (pill) */}
//             <div className="mt-3">
//                 <div className="alloc-toggle d-inline-flex rounded-pill p-1">
//                     <button
//                         type="button"
//                         aria-pressed={formik.values.mode === "bulk"}
//                         className={`btn rounded-pill btn-alloc btn-alloc-bulk ${formik.values.mode === "bulk" ? "active" : ""}`}
//                         onClick={() => formik.setFieldValue("mode", "bulk")}
//                     >
//                         Bulk Allocation
//                     </button>
//                     <button
//                         type="button"
//                         aria-pressed={formik.values.mode === "split"}
//                         className={`btn rounded-pill btn-alloc btn-alloc-split ${formik.values.mode === "split" ? "active" : ""}`}
//                         onClick={() => formik.setFieldValue("mode", "split")}
//                     >
//                         Split Allocation
//                     </button>
//                 </div>
//             </div>


//             {/* Bulk mode */}
//             {formik.values.mode === "bulk" && (
//                 <div className="row g-3 align-items-end mt-3">
//                     <div className="col-lg-4">
//                         <label className="form-label fs-13">Quantity</label>
//                         <input
//                             type="number"
//                             name="quantity"
//                             className="form-control"
//                             placeholder="Enter quantity"
//                             value={formik.values.quantity as any}
//                             onChange={formik.handleChange}
//                         />
//                     </div>
//                     <div className="align-items-center col-lg-4 d-lg-flex gap-5 offset-lg-1">
//                         <div className="col-lg-12">
//                             <label className="form-label fs-13">Bin Location</label>
//                             <select
//                                 className="form-select"
//                                 name="bulkBin"
//                                 value={formik.values.bulkBin as any}
//                                 onChange={formik.handleChange}
//                             >
//                                 <option value="">Select Bin Location</option>
//                                 {filteredCombinations?.map((combo) => (
//                                     <option key={combo.bin_id} value={combo.bin_id}>
//                                         {combo.warehouse} &gt; {combo.zone} &gt; {combo.rack} &gt; {combo.bin}
//                                     </option>
//                                 ))}
//                             </select>
//                         </div>

//                         <div className="col-lg-5 text-lg-end">
//                             <button type="submit" className="btn btn-primary table-addbutton mt-4" disabled={!isBulkReady}>
//                                 Allocate Bulk
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* Split mode */}
//             {formik.values.mode === "split" && (
//                 <div className="mt-4">
//                     <div className="fs-18 fw-bold mb-2">Split Allocations</div>

//                     {formik.values.splits?.map((item: any, index: number) => (
//                         <div key={index} className="align-items-center card flex-row g-3 mb-2 row mt-2 border-1 br-8 p-3">
//                             <div className="col-lg-4 mt-0 mb-3 mb-lg-0">
//                                 <label className="form-label fs-13">Select Serial Number</label>
//                                 <select
//                                     className="form-select"
//                                     name={`splits[${index}].serial_id`}
//                                     value={item.serial_id}
//                                     onChange={(e) => {
//                                         const serial_id = e.target.value;
//                                         const serial_number =
//                                             availableSerializedUnits.find((u: any) => u.serial_id === serial_id)?.serial_number || "";
//                                         const newSplits = [...(formik.values.splits as any[])];
//                                         newSplits[index] = { ...newSplits[index], serial_id, serial_number };
//                                         formik.setFieldValue("splits", newSplits);
//                                     }}
//                                 >
//                                     <option value="">Select Serial Number</option>
//                                     {availableSerializedUnits?.map((unit: any) => (
//                                         <option key={unit.serial_id} value={unit.serial_id}>
//                                             {unit.serial_number}
//                                         </option>
//                                     ))}
//                                 </select>
//                             </div>

//                             <div className="col-lg-4 mt-0 mb-3 mb-lg-0">
//                                 <label className="form-label fs-13">Select Bin</label>
//                                 <select
//                                     name={`splits[${index}].binId`}
//                                     className="form-select"
//                                     value={item.binId}
//                                     onChange={formik.handleChange}
//                                 >
//                                     <option value="">Select Bin</option>
//                                     {filteredCombinations?.map((combo) => (
//                                         <option key={combo.bin_id} value={combo.bin_id}>
//                                             {combo.warehouse} &gt; {combo.zone} &gt; {combo.rack} &gt; {combo.bin}
//                                         </option>
//                                     ))}
//                                 </select>
//                             </div>

//                             <div className="col-lg-2 mt-0">
//                                 <label className="form-label d-none d-lg-block fs-13">&nbsp;</label>
//                                 <div>
//                                     <button
//                                         type="button"
//                                         className={`btn text-danger fs-1 ${index === 0 ? "disabled" : ""} border-0 p-0`}
//                                         onClick={() => removeSplit(index)}
//                                         aria-label="Remove row"
//                                         disabled={index === 0}
//                                     >
//                                         ×
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>
//                     ))}

//                     <button type="button" className="btn btn-add-split mt-2 br-8" onClick={addSplit}>
//                         + Add Bin Allocation
//                     </button>
//                 </div>
//             )}

//             {/* Footer actions */}
//             <div className="row mt-4">
//                 <div className="col-12 d-flex justify-content-between">
//                     {/* <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
//                         Back
//                     </button> */}
//                     {
//                         formik.values.mode === "split" && (
//                             <button type="submit" className="btn btn-primary table-addbutton" disabled={!isSplitReady}>
//                                 Submit Allocation
//                             </button>
//                         )
//                     }
//                 </div>
//             </div>
//             <style>{`
//                 /* container */
//                 .alloc-toggle {
//                   background: #f3f4f6;           /* light gray track */
//                   border: 1px solid #e5e7eb;
//                 }

//                 /* base button */
//                 .btn-alloc {
//                   margin: 2px;
//                   min-width: 180px;              /* keeps both tabs same width */
//                   font-weight: 600;
//                   background: #f3f4f6;           /* default: light gray bg */
//                   color: #4b5563;                /* default: dark gray text */
//                   border: 1px solid #e5e7eb;
//                   transition: transform .12s ease, background-color .12s ease, color .12s ease, box-shadow .12s ease;
//                 }

//                 /* hover state (both) */
//                 .btn-alloc:hover {
//                   background: #ffffff;           /* white on hover */
//                   color: #000000;                /* black text on hover */
//                   transform: scale(1.03);        /* small scale increase */
//                 }

//                 /* active = BULK (clicked) */
//                 .btn-alloc-bulk.active {
//                   background: #eff6ff;           /* bg */
//                   color: #1447e6;                /* text */
//                   border-color: #bedbff;         /* border */
//                   box-shadow: 0 4px 12px rgba(20, 71, 230, 0.12);
//                 }

//                 /* active = SPLIT (clicked) */
//                 .btn-alloc-split.active {
//                   background: #faf5ff;           /* bg */
//                   color: #8200db;                /* text */
//                   border-color: #e9d4ff;         /* border */
//                   box-shadow: 0 4px 12px rgba(130, 0, 219, 0.12);
//                   }

//                   /* Add Bin Allocation button (neutral by default, darker on hover) */
//                     .btn-add-split{
//                       background:#f3f4f6;          /* light gray bg */
//                       color:#4b5563;               /* dark gray text */
//                       border:1px solid #d1d5db;    /* gray border */
//                       font-weight:600;
//                       transition: background-color .12s ease, color .12s ease, border-color .12s ease;
//                     }

//                     .btn-add-split:hover,
//                     .btn-add-split:focus{
//                       background:#ffffff;          /* “light white” on hover */
//                       color:#000000;               /* black text */
//                       border-color:#000000;        /* black border */
//                     }
//             `}</style>
//         </form>
//     );
// };

// export default AllocationToBins;

